import { ChangeDetectorRef, Component, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { CarrierService } from '../../user-dashboard/shared/carrier.service';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { concatMap, filter, finalize, flatMap, map, tap } from 'rxjs/operators';
import { LoaderService } from '../../shared/services/loader.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NavigationService } from '../../shared/services/navigation.service';
import { of, Subject, Subscription } from 'rxjs';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { TripService } from '../shared/trip.service';
import { Trip } from '../shared/trip.model';
import * as moment from 'moment';
import { Moment } from 'moment';
import { AngularFireDatabase } from '@angular/fire/database';
import { TripDevicePath } from '../shared/trip-path.model';
import { FIREBASE_CONFIG } from '../../core/firebase.config';
import { DistanceUnit } from 'app/shared/distance-unit';
import { Stats } from '../shared/stat.model';
import { QuickFilter } from '../shared/quick-filter';
import { Region } from '../../user-dashboard/shared/region.model';
import { RegionService } from '../../user-dashboard/shared/region.service';
import { TripManagementService } from '../shared/trip-management-service';
import { MapManagementService } from '../shared/map-management-service';
import { TripSortableColumn } from '../shared/trip-sortable-column';
import { SortDirection } from '../../shared/sort-direction';
import { TripStatus } from '../shared/trip-status';
import LatLngLiteral = google.maps.LatLngLiteral;
import Map = google.maps.Map;
import { DateTimezoneService } from '../shared/date-timezone.service';

@Component({
  selector: 'app-load-tracking-monitoring',
  templateUrl: './load-tracking-monitoring.component.html',
  styleUrls: ['./load-tracking-monitoring.component.scss']
})
export class LoadTrackingMonitoringComponent implements OnInit, OnChanges, OnDestroy {

  public carriers: Carrier[] = [];
  private _selectedCarrier: Carrier;

  public regions: Region[] = [];
  private _selectedRegion: Region;

  private _selectedTrip: Trip = null;

  public singleTrip = false;

  public carrierIdFromQueryParams = null;
  public regionIdFromQueryParams = null;
  public tripIdFromQueryParams    = null;

  public canShowDebug = false;
  public overviewActive = true;
  public showBucket = true;

  // Bucket
  public bucketFilterTab = true;
  public tripsScrolled = false;
  public quickFilter: QuickFilter = QuickFilter.IN_PROGRESS;
  public bucketTrips: Trip[] = [];

  public positionSubscriptions: { customerId: string, subscription: Subscription }[] = [];
  public tripsRemovalSubscription: Subscription;

  // Map
  public _showAchievedPath = true;
  public _showForecastPath = true;
  public forecastPathReady = true;
  public excludeIsolatedPings         = true;
  public hidePingsAfterDestinationHit = false;
  public keepPingsWithHighAccuracy    = false;
  public debug                        = false;
  public DistanceUnit = DistanceUnit;
  public _distanceUnit = DistanceUnit.KILOMETERS;
  public latestTripDetailsRefreshedAt: Moment;
  public latestTripDistanceRefreshedAt: Moment;
  public detailedMode = false;
  public refreshing = false;
  public latLngLiteralSubject = new Subject<LatLngLiteral>();
  public zoomSubject          = new Subject<number>();

  private statsInterval: any;

  public map: Map;

  // Overview
  public date = moment().format('YYYY-MM-DD');
  public stats: Stats = new Stats(0, 0, 0, 0, 0, 0);

  // Common
  public sort: TripSortableColumn = TripSortableColumn.STARTED_AT;
  public sortDirection: SortDirection = SortDirection.ASC;
  public statusSortDirection: TripStatus = TripStatus.PLANNED;

  get selectedCarrier(): Carrier {
    return this._selectedCarrier;
  }

  set selectedCarrier(value: Carrier) {
    this._selectedCarrier = value;

    if (value) {
      this.selectedCarrierChanged();
    } else {
      this.unselectCarrier();
    }
  }

  get selectedRegion(): Region {
    return this._selectedRegion;
  }

  set selectedRegion(value: Region) {
    this._selectedRegion = value;

    if (value) {
      this.selectedRegionChanged();
    } else {
      this.unselectRegion();
    }
  }

  get selectedTrip(): Trip {
    return this._selectedTrip;
  }

  set selectedTrip(value: Trip) {
    this._selectedTrip = value;

    if (value) {
      this.selectedTripChanged();
    }
  }
  get showAchievedPath(): boolean {
    return this._showAchievedPath;
  }

  set showAchievedPath(value: boolean) {
    this._showAchievedPath = value;

    if (value) {
      this.changeShowAchievedPath();
    }
  }
  get showForecastPath(): boolean {
    return this._showForecastPath;
  }

  set showForecastPath(value: boolean) {
    this._showForecastPath = value;

    if (value) {
      this.changeShowForecastPath();
    }
  }

  get distanceUnit(): DistanceUnit {
    return this._distanceUnit;
  }
  set distanceUnit(value: DistanceUnit) {
    this._distanceUnit = value;

    if (value) {
      this.distanceUnitChanged(value);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthenticationService,
    private carrierService: CarrierService,
    private tripService: TripService,
    private regionService: RegionService,
    private tripManagementService: TripManagementService,
    private mapManagementService: MapManagementService,
    private navigation: NavigationService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private errorHandler: ErrorHandlerService,
    private cdr: ChangeDetectorRef,
    private af: AngularFireDatabase,
    public timeZoneService: DateTimezoneService
  ) {
    this.route.data.subscribe(data => this.overviewActive = data.overviewActive);
  }

  public ngOnInit(): void {
    this.route.queryParams.subscribe(queryParams => {
      this.carrierIdFromQueryParams = queryParams.carrierId;
      this.regionIdFromQueryParams = queryParams.regionId;
      this.tripIdFromQueryParams = queryParams.tripId;

      this.fetchSelectedTrip();
    });

    if (this.authService.canMonitorApi()) {
      this.canShowDebug = true;
    }

    if (this.authService.canManageTrips()) {
      this.fetchRegions();
      this.fetchCarriers();
    } else if (this.authService.canManageCarrierTrips()) {
      this.fetchCarrier();
    } else {
      this.toastr.error('You are not allowed to view this page');
      this.navigation.home().go();
      return;
    }

    this.statsInterval = setInterval(() => this.refreshTripStats(), 60000);
  }

  private fetchSelectedTrip(): void {
    if (this.tripIdFromQueryParams == null) {
      return;
    }

    this.loader.startLoad()
    this.tripService.getById(this.tripIdFromQueryParams)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(trip => {
        this.tripIdFromQueryParams = trip.tripId;
        this.bucketTrips = [trip];
        this.selectedTrip = trip;
      }, e => this.errorHandler.handleHttpError(e, 'realtime-tracking.load-tracking-map.errors.fetch-trip'));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.carrier && changes.carrier.currentValue) {
      this.selectedCarrierChanged();
    }
    if (changes.carrier && !changes.carrier.currentValue && changes.carrier.previousValue) {
      this.unselectCarrier();
    }
  }

  private updateCurrentRoute() {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: {
          carrierId: this.selectedCarrier?.id,
          regionId: this.selectedCarrier ? null : this.selectedRegion?.id,
        },
      }
    );
  }

  private unselectCarrier(): void {
    this.resetFbSubscriptions();
    this.bucketTrips = [];
    this.unselectTrip();
    this.refreshTripStats();
    this.updateCurrentRoute();
  }

  private unselectRegion(): void {
    this.resetFbSubscriptions();
    this.bucketTrips = [];
    this.unselectTrip();
    this.refreshTripStats();
    this.updateCurrentRoute();
  }

  private selectedCarrierChanged(): void {
    this.resetFbSubscriptions();
    this.watchTripsRemoval();
    this.refreshTripStats();
    this.regionService.getByCarrier(this.selectedCarrier.id).subscribe(region => this.selectedRegion = region);
    this.updateCurrentRoute();
  }

  private selectedRegionChanged(): void {
    this.resetFbSubscriptions();
    this.watchTripsRemovalAll();
    this.refreshTripStats();
    this.distanceUnit = this.selectedRegion.distanceUnit;
    this.fetchCarriers(this.selectedRegion);
    this.updateCurrentRoute();
  }

  public unselectTrip(): void {
    this.selectedTrip = null;
    this.latestTripDetailsRefreshedAt = null;
    this.latestTripDistanceRefreshedAt = null;
    this.detailedMode = false;
  }

  private selectedTripChanged(): void {

    this.latestTripDetailsRefreshedAt = null;
    this.latestTripDistanceRefreshedAt = null;
    this.detailedMode = false;
    const positionsObservable = this.tripManagementService.initTrip(this.selectedTrip, false);

    this.loader.startLoadAndCloseWhenComplete(positionsObservable.pipe(
      tap(([origin, destination]) => {
        this.selectedTrip.originPosition = origin;
        this.selectedTrip.destPosition   = destination;
      }),
      concatMap(() => {
        const refresh = this.tripManagementService.refreshTripDetails(
          this.selectedTrip,
          false,
          this.detailedMode,
          false,
          true,
          this.showAchievedPath,
          this.showForecastPath,
          this.latestTripDetailsRefreshedAt,
          this.latestTripDistanceRefreshedAt,
          this.distanceUnit,
          this.keepPingsWithHighAccuracy,
          this.excludeIsolatedPings,
          this.hidePingsAfterDestinationHit
        )
        this.latestTripDetailsRefreshedAt = refresh.latestTripDetailsRefreshedAt;
        this.latestTripDistanceRefreshedAt = refresh.latestTripDistanceRefreshedAt;
        return refresh.observable;
      }),
    ))
      .subscribe(() => {
        if (this.selectedTrip) {
          this.selectedTrip.timelineParamsInitialized = true;
          setTimeout(() => this.centerView(), 1000);
        }
        this.cdr.detectChanges();
      });
  }

  public forceRefreshTripDetails(trip: Trip): void {
    this.refreshing = true;
    this.loader.startLoadAndCloseWhenComplete(
      this.tripManagementService.refreshTripDetails(
        trip,
        false,
        this.detailedMode,
        true,
        false,
        this.showAchievedPath,
        this.showForecastPath,
        this.latestTripDetailsRefreshedAt,
        this.latestTripDistanceRefreshedAt,
        this.distanceUnit,
        this.keepPingsWithHighAccuracy,
        this.excludeIsolatedPings,
        this.hidePingsAfterDestinationHit
      )
      .observable
    )
    .subscribe(() => {
      this.refreshing = false;
      if (this.selectedTrip) {
        this.selectedTrip.timelineParamsInitialized = true;
        setTimeout(() => this.centerView(), 1000);
      }
      this.cdr.detectChanges();
    });
  }

  private refreshTripStats(): void {
    if (this.selectedCarrier != null) {
      this.tripService.getStatsByCarrier(this.selectedCarrier).subscribe(stats => this.stats = {...stats});
    } else if (this.selectedRegion != null) {
      this.tripService.getStatsByRegion(this.selectedRegion).subscribe(stats => this.stats = {...stats});
    } else {
      this.stats = new Stats(0, 0, 0, 0, 0, 0);
    }
  }

  private changeShowAchievedPath(): void {
    if (this.showAchievedPath && this.selectedTrip) {
      this.loader.startLoadAndCloseWhenComplete(
        this.tripManagementService.refreshTripAchievedPath(
          this.selectedTrip,
          false,
          this.keepPingsWithHighAccuracy,
          this.excludeIsolatedPings,
          this.hidePingsAfterDestinationHit
        )
      ).subscribe(() => {
        this.cdr.detectChanges();
      });
    }
  }

  private changeShowForecastPath(): void {
    if (this.showForecastPath && this.selectedTrip) {
      this.loader.startLoadAndCloseWhenComplete(
        this.tripManagementService.refreshTripForecastPath(
          this.selectedTrip,
          false,
          this.distanceUnit,
          this.singleTrip
        )
      ).subscribe(() => {
        this.forecastPathReady = true;
        this.cdr.detectChanges();
      });
    }
  }

  public clearTrips(): void {
    this.replaceBucketWithTrips([]);
  }

  public addTripsToBucket(trips: Trip[]): void {
    const bucketTripsIds = this.bucketTrips.map(t => t.tripId);
    const newTrips = trips.filter(t => !bucketTripsIds.includes(t.tripId));
    this.bucketTrips = this.bucketTrips.concat(newTrips);

    this.bucketTrips.filter(t => t.isInProgress())
      .forEach(t => this.watchTripPosition(t));
  }

  private replaceBucketWithTrips(trips: Trip[]): void {
    this.resetFbPositionsSubscriptions();
    this.unselectTrip();

    this.bucketTrips = trips;

    this.bucketTrips.filter(t => t.isInProgress())
      .forEach(t => this.watchTripPosition(t));
  }

  public removeTrip(trip: Trip): void {
    if (this.selectedTrip === trip) {
      this.unselectTrip();
    }

    const tripIndex = this.bucketTrips.findIndex(t => t.tripId === trip.tripId);
    if (tripIndex !== -1) {
      this.bucketTrips.splice(tripIndex, 1);
    }

    this.unwatchTripPosition(trip);
    this.cdr.detectChanges();
  }

  private watchTripsRemoval(): void {
    const tripsRef = this.af.database.ref(`/geotracking/${FIREBASE_CONFIG.env}/trips/live/${this.selectedCarrier.id}`);
    const trips = this.af.list(tripsRef);

    this.tripsRemovalSubscription = trips.stateChanges(['child_removed'])
      .pipe(
        map(action => action.key),
        map(customerId => this.bucketTrips.find(t => t.customerId === customerId)),
        filter(t => !!t)
      )
      .subscribe(trip => {
        console.log('trip position removed => ', trip);
        this.tripManagementService.refreshTrip(trip, false);
      });
  }

  private watchTripsRemovalAll(): void {
    this.carriers.map(carrier => {
      const tripsRef = this.af.database.ref(`/geotracking/${FIREBASE_CONFIG.env}/trips/live/${carrier.id}`);
      const trips = this.af.list(tripsRef);

      this.tripsRemovalSubscription = trips.stateChanges(['child_removed'])
        .pipe(
          map(action => action.key),
          map(customerId => this.bucketTrips.find(t => t.customerId === customerId)),
          filter(t => !!t)
        )
        .subscribe(trip => {
          console.log('trip position removed => ', trip);
          this.tripManagementService.refreshTrip(trip, false);
        });
    })
  }

  private watchTripPosition(trip: Trip): void {
    const subscription = this.tripManagementService.getTripPositionObservable(trip)
      .subscribe(p => {
        const tripDevicePath = trip.achievedPaths
          .find((path: TripDevicePath) => path.deviceId === p.deviceId);

        if (tripDevicePath !== undefined) {
          const lastPath = tripDevicePath.getLastPath();
          lastPath.addPing(p);
          lastPath.setLastPing(p);
        }
        trip.latestPing = p;

        this.cdr.detectChanges();
      });
    this.positionSubscriptions.push({ customerId: trip.customerId, subscription });
  }

  private unwatchTripPosition(trip: Trip): void {
    const subscriptionIndex = this.positionSubscriptions.findIndex(s => s.customerId === trip.customerId);

    if (subscriptionIndex !== -1) {
      this.positionSubscriptions[subscriptionIndex].subscription.unsubscribe();
      this.positionSubscriptions.splice(subscriptionIndex, 1)
    }
  }

  private resetFbPositionsSubscriptions(): void {
    this.positionSubscriptions.forEach(s => s.subscription.unsubscribe());
    this.positionSubscriptions = [];
  }

  private resetFbSubscriptions(): void {
    this.resetFbPositionsSubscriptions();

    if (this.tripsRemovalSubscription) {
      this.tripsRemovalSubscription.unsubscribe();
    }
  }

  public ngOnDestroy(): void {
    clearInterval(this.statsInterval);
    this.resetFbSubscriptions();
  }

  public centerChanged(latlng: LatLngLiteral): void {
    this.latLngLiteralSubject.next(latlng);
  }

  public zoomChanged(zoom: number): void {
    this.zoomSubject.next(zoom);
  }

  public goToMarker(latLng: LatLngLiteral): void {
    this.map.panTo(latLng);
    this.cdr.detectChanges();
  }

  private distanceUnitChanged(distanceUnit: DistanceUnit): void {
    if (this.selectedTrip && this.tripManagementService.metricsDistance.has(this.selectedTrip.tripId) &&
      this.tripManagementService.imperialDistance.has(this.selectedTrip.tripId)) {
      let distance
      if (distanceUnit === DistanceUnit.MILES) {
        distance = this.tripManagementService.imperialDistance.get(this.selectedTrip.tripId);
      } else {
        distance = this.tripManagementService.metricsDistance.get(this.selectedTrip.tripId);
      }
      this.selectedTrip.distanceToArrival = distance;
      this.cdr.detectChanges();
    }
  }

  public onMapReady(initializedMap: Map): void {
    this.map = initializedMap;
  }

  public centerView(): void {
    this.mapManagementService.centerView(this.selectedTrip, this.map, false);
  }

  private fetchRegions(): void {
    of({})
      .pipe(
        tap(() => this.loader.startLoad()),
        flatMap(() => this.regionService.getAll()),
        finalize(() => this.loader.stopLoad()),
      )
      .subscribe(regions => {
        this.regions = regions;
        if (this.regionIdFromQueryParams) {
          this.selectedRegion = regions.find((region) => region.id === this.regionIdFromQueryParams);
        }
      }, e => this.errorHandler.handleHttpError(e, 'realtime-tracking.load-tracking-map.errors.fetch-regions'));
  }

  private fetchCarriers(region: Region = null): void {
    if (!region) {
      this.loader.startLoad()
          this.carrierService.getAllLight()
          .pipe(finalize(() => this.loader.stopLoad())
        )
        .subscribe(carriers => {
          this.carriers = carriers;
          if (this.carrierIdFromQueryParams && !this.selectedCarrier) {
            this.selectedCarrier = carriers.find((carrier) => carrier.id === this.carrierIdFromQueryParams);
          }
        }, e => this.errorHandler.handleHttpError(e, 'realtime-tracking.load-tracking-map.errors.fetch-carriers'));
    } else {
      of({})
        .pipe(
          tap(() => this.loader.startLoad()),
          flatMap(() => this.carrierService.getLightByRegion(region)),
          finalize(() => this.loader.stopLoad()),
        )
        .subscribe(carriers => {
          this.carriers = carriers;
          if (this.carrierIdFromQueryParams && !this.selectedCarrier) {
            this.selectedCarrier = carriers.find((carrier) => carrier.id === this.carrierIdFromQueryParams);
          }
        }, e => this.errorHandler.handleHttpError(e, 'realtime-tracking.load-tracking-map.errors.fetch-carriers'));
    }
  }

  private fetchCarrier(): void {
    this.loader.startLoad();
    this.carrierService.getSelfCarrier()
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(carrier => {
        this.carriers = [carrier];
        this.selectedCarrier = carrier;
      }, e => this.errorHandler.handleHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.errors.fetch-self-carrier'));
  }

  public switchMode(): void {
    this.overviewActive = !this.overviewActive;
    if (!this.overviewActive) {
      setTimeout(() => this.centerView(), 1000);
    }
  }

  public setBucketTab(f: QuickFilter): void {
    this.bucketFilterTab = true;
    this.quickFilter = f;
  }

  public hideBucket(): void {
    this.showBucket = false;
  }
}
